/*
 * Settings, Agency, Workspaces: adding an Email Bison workspace by its API key (LEA-476, slice 4).
 * Two steps on one sheet, as People's invite form is laid out: paste the key and check it, which asks
 * Email Bison whose it is and writes nothing; then the one step that answer allows. The key lives in
 * this form's state only: a password field, never echoed, gone when the form closes. The routes
 * decide who may add (`org_admin`); this form only draws their answers.
 */
import { useId, useState } from "react";
import {
  type Added,
  AddedSchema,
  type Answer,
  addedCopy,
  type Identified,
  IdentifiedSchema,
  identifiedCopy,
  KEY_SOURCE,
  keyRefusal,
} from "./workspace-copy.ts";

/** A POST to a Workspaces route, as an `Answer`; status 0 when the network failed. */
export async function post(path: string, body: object): Promise<Answer> {
  try {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return {
      status: response.status,
      body: await response.json().catch((): unknown => null),
      retryAfter: response.headers.get("retry-after"),
    };
  } catch {
    return { status: 0, body: null, retryAfter: null };
  }
}

/** The checked key's answer as the form holds it, after an add left the key unsaved. */
function keyless(found: Identified, added: Added): Identified {
  return {
    providerWorkspaceId: found.providerWorkspaceId,
    name: added.name,
    status: "added",
    workspaceId: added.workspaceId,
    hasKey: false,
  };
}

function FoundLine(props: { found: Identified; missing: string | null }) {
  const { sentence } = identifiedCopy(props.found);
  return (
    <div className="lcr-workspace-found" role={props.missing ? "alert" : "status"}>
      <p>
        Email Bison says this key opens{" "}
        <strong className="lcr-workspace-found-name">{props.found.name}</strong>.
      </p>
      <p>{props.missing ?? sentence}</p>
    </div>
  );
}

export function AddWorkspaceForm(props: {
  readonly onCancel: () => void;
  /** The add finished with its key stored; the section closes the form and says this. */
  readonly onAdded: (message: string) => void;
  /** Something was written: the list is read again. */
  readonly onChanged: () => void;
}) {
  const id = useId();
  const [key, setKey] = useState("");
  const [found, setFound] = useState<Identified | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [busy, setBusy] = useState<"check" | "add" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const step = found ? identifiedCopy(found).step : null;

  const check = async (): Promise<void> => {
    if (key.trim() === "") {
      setError("Paste the key first.");
      return;
    }
    setBusy("check");
    setError(null);
    const answer = await post("/api/work/workspaces/identify", { token: key });
    setBusy(null);
    const parsed = IdentifiedSchema.safeParse(answer.body);
    if (answer.status === 200 && parsed.success) setFound(parsed.data);
    else setError(keyRefusal(answer));
  };

  const add = async (target: Identified): Promise<void> => {
    setBusy("add");
    setError(null);
    const answer = await post("/api/work/workspaces", {
      token: key,
      providerWorkspaceId: target.providerWorkspaceId,
    });
    setBusy(null);
    const parsed = AddedSchema.safeParse(answer.body);
    if (answer.status !== 200 || !parsed.success) {
      setError(keyRefusal(answer));
      return;
    }
    props.onChanged();
    const copy = addedCopy(parsed.data);
    if (!copy.keyMissing) return props.onAdded(copy.message);
    setMissing(copy.message);
    setFound(keyless(target, parsed.data));
  };

  return (
    <form
      className="lcr-people-invite lcr-workspace-add"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (busy !== null) return;
        void (found && step ? add(found) : check());
      }}
    >
      <h2 id={`${id}-title`} className="lcr-people-invite-title">
        Add a Bison workspace
      </h2>
      <p className="lcr-settings-note">{KEY_SOURCE}</p>
      <label className="lcr-people-field">
        API key
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore="true"
          data-lpignore="true"
          // biome-ignore lint/a11y/noAutofocus: the form opens because the person asked to paste a key.
          autoFocus
          value={key}
          aria-invalid={error !== null || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => {
            setKey(event.target.value);
            setFound(null);
            setMissing(null);
            setError(null);
          }}
        />
      </label>
      {error && (
        <p id={`${id}-error`} className="lcr-people-error" role="alert">
          {error}
        </p>
      )}
      {found && <FoundLine found={found} missing={missing} />}
      <div className="lcr-people-actions">
        {(!found || step) && (
          <button type="submit" className="lcr-primary" disabled={busy !== null}>
            {buttonLabel(busy, found ? step?.label : undefined, missing !== null)}
          </button>
        )}
        <button type="button" className="lcr-ghost" onClick={props.onCancel}>
          {found && !step ? "Close" : "Cancel"}
        </button>
      </div>
    </form>
  );
}

function buttonLabel(
  busy: "check" | "add" | null,
  step: string | undefined,
  retrying: boolean,
): string {
  if (busy === "check") return "Checking…";
  if (busy === "add") return "Saving…";
  if (step === undefined) return "Check key";
  return retrying ? "Try saving the key again" : step;
}
